/**
 * Permission Registry
 * 
 * Frontend registry for permissions and permission checking.
 * Part of Core Hardening Phase 2.
 */

import React from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  getMyPermissions,
  getPermissions,
  checkPermissions,
  type Permission,
  type Role,
  type PermissionCheckResult,
} from '../../api/platform';
import { useToken } from '../../stores/authStore';

/**
 * Permission registry state
 */
interface PermissionRegistryState {
  // User's permissions
  myPermissions: string[];
  myRoles: Role[];
  
  // All registered permissions (for admin UI)
  allPermissions: Permission[];
  allRoles: Role[];
  
  // State
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // Actions
  loadMyPermissions: () => Promise<void>;
  loadAllPermissions: () => Promise<void>;
  checkPermission: (permission: string) => boolean;
  checkPermissions: (permissions: string[]) => boolean;
  checkPermissionsAsync: (permissions: string[]) => Promise<PermissionCheckResult>;
  hasAnyPermission: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
  reset: () => void;
}

/**
 * Permission registry store
 */
export const usePermissionRegistry = create<PermissionRegistryState>()(
  subscribeWithSelector((set, get) => ({
    myPermissions: [],
    myRoles: [],
    allPermissions: [],
    allRoles: [],
    isLoading: false,
    error: null,
    lastUpdated: null,

    loadMyPermissions: async () => {
      const { isLoading, lastUpdated } = get();
      if (isLoading) return;
      if (lastUpdated && Date.now() - lastUpdated.getTime() < 5000) return;

      set({ isLoading: true, error: null });
      
      try {
        const data = await getMyPermissions();
        set({
          myPermissions: data.permissions || [],
          myRoles: data.roles || [],
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (error: any) {
        // Handle 401 gracefully - user is not authenticated, don't treat as error
        const is401 = error?.response?.status === 401 || error?.status === 401;
        set({
          myPermissions: [],
          myRoles: [],
          isLoading: false,
          error: is401 ? null : (error instanceof Error ? error.message : 'Failed to load permissions'),
        });
      }
    },

    loadAllPermissions: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const data = await getPermissions();
        set({
          allPermissions: data.permissions,
          allRoles: data.roles,
          isLoading: false,
        });
      } catch (error) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load permissions',
        });
      }
    },

    checkPermission: (permission: string) => {
      const { myPermissions } = get();
      if (!myPermissions) return false;
      if (myPermissions.includes('admin')) return true;
      return myPermissions.includes(permission);
    },

    checkPermissions: (permissions: string[]) => {
      const { myPermissions } = get();
      if (!myPermissions) return false;
      if (myPermissions.includes('admin')) return true;
      return permissions.every(p => myPermissions.includes(p));
    },

    checkPermissionsAsync: async (permissions: string[]) => {
      return checkPermissions(permissions);
    },

    hasAnyPermission: (permissions: string[]) => {
      const { myPermissions } = get();
      if (!myPermissions) return false;
      if (myPermissions.includes('admin')) return true;
      return permissions.some(p => myPermissions.includes(p));
    },

    isAdmin: () => {
      const { myPermissions, myRoles } = get();
      return (myPermissions && myPermissions.includes('admin')) || (myRoles && myRoles.some(r => r.id === 'admin'));
    },

    reset: () => {
      set({
        myPermissions: [],
        myRoles: [],
        allPermissions: [],
        allRoles: [],
        isLoading: false,
        error: null,
        lastUpdated: null,
      });
    },
  }))
);

/**
 * Hook to check permissions
 */
export function usePermissions() {
  const token = useToken();
  const {
    myPermissions,
    myRoles,
    isLoading,
    error,
    loadMyPermissions,
    checkPermission,
    checkPermissions,
    hasAnyPermission,
    isAdmin,
  } = usePermissionRegistry();

  // Load on mount only if authenticated
  React.useEffect(() => {
    if (token && !isLoading && (!myPermissions || myPermissions.length === 0)) {
      loadMyPermissions();
    }
  }, [token, isLoading, myPermissions, loadMyPermissions]);

  return {
    permissions: myPermissions,
    roles: myRoles,
    isLoading,
    error,
    checkPermission,
    checkPermissions,
    hasAnyPermission,
    isAdmin: isAdmin(),
    refresh: loadMyPermissions,
  };
}

/**
 * Hook to check a single permission
 */
export function usePermission(permission: string): boolean {
  const { myPermissions, loadMyPermissions, isLoading } = usePermissionRegistry();
  
  // Load on mount
  React.useEffect(() => {
    if (!isLoading && myPermissions.length === 0) {
      loadMyPermissions();
    }
  }, []);
  
  if (myPermissions.includes('admin')) return true;
  return myPermissions.includes(permission);
}

/**
 * Hook for admin-only permission management
 */
export function useAllPermissions() {
  const {
    allPermissions,
    allRoles,
    isLoading,
    error,
    loadAllPermissions,
  } = usePermissionRegistry();

  // Load on mount
  React.useEffect(() => {
    if (!isLoading && allPermissions.length === 0) {
      loadAllPermissions();
    }
  }, []);

  return {
    permissions: allPermissions,
    roles: allRoles,
    isLoading,
    error,
    refresh: loadAllPermissions,
  };
}

// Re-export types from platform API
export type { Permission, Role, PermissionCheckResult } from '../../api/platform';

// Define PermissionCategory locally since it doesn't exist in platform API
export interface PermissionCategory {
  id: string;
  name: string;
  description?: string;
  permissions: Permission[];
}
